import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';
import { Button, Icon, Label } from "@gravity-ui/uikit";
import { ArrowLeft, Plus } from "@gravity-ui/icons";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {
    getAcceptAuthConfig,
    PARTNER_TYPES_DETAIL_ENDPOINT,
    WORKSHOPS_INFO_ENDPOINT,
    PARTNERS_ENDPOINT,
    PARTNER_DETAIL_ENDPOINT
} from "../../../api/api";
import { UpdateModal, DeleteModal } from "../../../components/Modal/Modals";
import { Form } from "react-bootstrap";
import { AddButtonMaxWidth, DeleteButtonIcon, EditButtonIcon } from "../../../components/Button/Button";


const PartnerForm = ({ submitting, onSubmit, partnerData, setPartnerData, errors, setOpen, isEdit = false }) => {
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPartnerData({ ...partnerData, [name]: value });
    };

    const handleFileChange = (e) => {
        setPartnerData({ ...partnerData, photo: e.target.files[0] });
    };

    return (
        <Form onSubmit={onSubmit}>
            <Form.Group controlId="formPartnerUrl" className="mb-3">
                <Form.Label className="required">URL партнера</Form.Label>
                <Form.Control
                    type="url"
                    name="url"
                    value={partnerData.url}
                    onChange={handleInputChange}
                    required
                    isInvalid={!!errors.url}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.url}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group controlId="formPartnerPhoto" className="mb-3">
                <Form.Label>{isEdit ? "Логотип партнера ('png, jpg', 'webp') - без фона" : "Логотип партнера ('png', jpg', 'webp') - без фона"}</Form.Label>
                <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    required={!isEdit}
                    isInvalid={!!errors.photo}
                />
                <Form.Control.Feedback type="invalid">
                    {errors.photo}
                </Form.Control.Feedback>
            </Form.Group>

            <div className="line-btn-group mt-3">
                <Button view="normal" onClick={() => setOpen(false)} width="max">
                    Отменить
                </Button>
                <Button type="submit" view="action" loading={submitting} width="max">
                    {submitting ? 'Сохранение...' : 'Сохранить'}
                </Button>
            </div>
        </Form>
    );
};

const PartnerCard = ({ partner, onDelete, onEdit }) => {
    const [openDelete, setOpenDelete] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [editData, setEditData] = useState({
        url: partner.url,
        photo: null
    });

    return (
        <div className="partner-card">
            <div className="partner-actions">
                <EditButtonIcon onClick={() => setOpenEdit(true)} />
                <DeleteButtonIcon onClick={() => setOpenDelete(true)} />
            </div>

            <div className="partner-logo-container">
                <img
                    src={partner.photo}
                    alt={partner.url}
                    className="partner-logo"
                />
            </div>

            <DeleteModal
                type="партнера"
                name={partner.url}
                onDelete={() => {
                    onDelete(partner.id);
                    setOpenDelete(false);
                }}
                open={openDelete}
                setOpen={setOpenDelete}
            />

            <UpdateModal open={openEdit} setOpen={setOpenEdit}>
                <PartnerForm
                    submitting={false}
                    onSubmit={(e) => {
                        e.preventDefault();
                        onEdit(partner.id, editData);
                        setOpenEdit(false);
                    }}
                    partnerData={editData}
                    setPartnerData={setEditData}
                    errors={{}}
                    setOpen={setOpenEdit}
                    isEdit={true}
                />
            </UpdateModal>
        </div>
    );
};

const PartnerTypePage = ({ user }) => {
    const { workshopId, partnerTypeId } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [partnerType, setPartnerType] = useState(null);
    const [workshop, setWorkshop] = useState(null);
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [partnerData, setPartnerData] = useState({
        url: '',
        photo: null,
        partner_type: partnerTypeId
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                const workshopResponse = await axios.get(
                    `${WORKSHOPS_INFO_ENDPOINT}${workshopId}/`,
                    getAcceptAuthConfig()
                );
                setWorkshop(workshopResponse.data);

                const partnerTypeResponse = await axios.get(
                    PARTNER_TYPES_DETAIL_ENDPOINT(partnerTypeId),
                    getAcceptAuthConfig()
                );
                setPartnerType(partnerTypeResponse.data);
            } catch (error) {
                console.error('Ошибка при загрузке данных:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [workshopId, partnerTypeId]);

    const handleBack = () => {
        navigate(`/account/partners?workshop=${workshopId}`);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const formData = new FormData();
        formData.append('partner_type', partnerTypeId);
        formData.append('url', partnerData.url);
        if (partnerData.photo) formData.append('photo', partnerData.photo);

        try {
            await axios.post(PARTNERS_ENDPOINT, formData, getAcceptAuthConfig());
            const partnerTypeResponse = await axios.get(
                PARTNER_TYPES_DETAIL_ENDPOINT(partnerTypeId),
                getAcceptAuthConfig()
            );
            setPartnerType(partnerTypeResponse.data);
            setOpenCreateModal(false);
            setPartnerData({
                url: '',
                photo: null,
                partner_type: partnerTypeId
            });
        } catch (error) {
            setErrors(error.response?.data || {});
            console.error('Ошибка при создании партнера:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeletePartner = async (partnerId) => {
        try {
            await axios.delete(
                PARTNER_DETAIL_ENDPOINT(partnerId),
                getAcceptAuthConfig()
            );
            const partnerTypeResponse = await axios.get(
                PARTNER_TYPES_DETAIL_ENDPOINT(partnerTypeId),
                getAcceptAuthConfig()
            );
            setPartnerType(partnerTypeResponse.data);
        } catch (error) {
            console.error('Ошибка при удалении партнера:', error);
        }
    };

    const handleEditPartner = async (partnerId, data) => {
        const formData = new FormData();
        formData.append('url', data.url);
        if (data.photo) formData.append('photo', data.photo);

        try {
            await axios.patch(
                PARTNER_DETAIL_ENDPOINT(partnerId),
                formData,
                getAcceptAuthConfig()
            );
            const partnerTypeResponse = await axios.get(
                PARTNER_TYPES_DETAIL_ENDPOINT(partnerTypeId),
                getAcceptAuthConfig()
            );
            setPartnerType(partnerTypeResponse.data);
        } catch (error) {
            console.error('Ошибка при редактировании партнера:', error);
        }
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="partner-type-page">
            <div className="d-flex align-items-center mb-4">
                <Button
                    view="flat"
                    onClick={handleBack}
                    size="l"
                    className="mr-3"
                >
                    <Icon data={ArrowLeft} size={18} />
                </Button>
                <h1 className="mb-0">Партнеры</h1>
            </div>

            <div className="bmm-card p-4 mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <h2 className="mb-0">{partnerType?.name}</h2>
                    <Label theme="info">
                        {workshop?.name} {workshop?.year}
                    </Label>
                </div>
            </div>

            <AddButtonMaxWidth
                className={"mb-3 mt-4"}
                text={"Добавить партнера"}
                onClick={() => setOpenCreateModal(true)}
            />

            <div>
                {partnerType?.partners?.length > 0 ? (
                    <div className="partners-grid">
                        {partnerType.partners.map(partner => (
                            <PartnerCard
                                key={partner.id}
                                partner={partner}
                                onDelete={handleDeletePartner}
                                onEdit={handleEditPartner}
                            />
                        ))}
                    </div>
                ) : (
                    <p>Пока нет партнеров этого типа</p>
                )}
            </div>

            <UpdateModal open={openCreateModal} setOpen={setOpenCreateModal}>
                <PartnerForm
                    submitting={submitting}
                    onSubmit={handleSubmit}
                    partnerData={partnerData}
                    setPartnerData={setPartnerData}
                    errors={errors}
                    setOpen={setOpenCreateModal}
                />
            </UpdateModal>
        </div>
    );
};

const mapStateToProps = state => ({
    user: state.auth.user,
});

export default connect(mapStateToProps)(PartnerTypePage);